// src/components/TaskDashboard.js
import React, { useState, useEffect } from 'react';
import CustomModal from './CustomModal'; 
import AssigneeAutocomplete from './AssigneeAutocomplete';
import ProjectAutocomplete from './ProjectAutocomplete'; // IMPORTIERT
// 'deleteTask' wurde entfernt, da es nicht mehr verwendet wird
import { saveTask } from '../services/firestoreService';
import { generateId } from '../services/utils'; // Import der sicheren ID-Funktion

// TaskModal als eigenständige Komponente auf der obersten Ebene.
const TaskModal = ({ task, onSave, onClose, companyUsers, teams, userId, setNotification, companyId }) => {
    const [title, setTitle] = useState(task?.title || '');
    const [description, setDescription] = useState(task?.description || '');
    const [assignee, setAssignee] = useState(task?.assignees?.[0] || null);
    const [dueDate, setDueDate] = useState(task?.dueDate || '');
    const [status, setStatus] = useState(task?.status || 'Offen');
    
    // Für Projekte: Wir speichern ID und Name, oder nutzen die ID für die Vorauswahl
    const [projectId, setProjectId] = useState(task?.projectId || '');
    const [projectName, setProjectName] = useState(task?.project || '');

    const creator = task?.creatorUserId ? companyUsers.find(u => u.uid === task.creatorUserId) : null;
    const creatorName = creator ? `${creator.firstName} ${creator.lastName}`.trim() : 'Unbekannt';

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!title.trim()) {
            setNotification({ message: 'Bitte geben Sie einen Titel für die Aufgabe ein.', type: 'error' });
            return;
        }

        let finalAssignee = assignee;
        if (!finalAssignee) {
            const creatorProfile = companyUsers.find(u => u.uid === userId);
            if (creatorProfile) {
                finalAssignee = {
                    id: userId,
                    name: `${creatorProfile.firstName || ''} ${creatorProfile.lastName || ''}`.trim(),
                    type: 'user'
                };
            }
        }

        const taskData = {
            id: task?.id || generateId(), // KORRIGIERT: crypto.randomUUID() ersetzt
            title, description,
            assignees: finalAssignee ? [finalAssignee] : [],
            dueDate, status, 
            project: projectName,   // Speichere den Namen für die Anzeige
            projectId: projectId,   // Speichere die ID für die Verknüpfung
        };
        onSave(taskData);
    };

    return (
        <CustomModal isOpen={true} onClose={onClose}>
            <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-grow">
                    <h2 className="text-2xl font-bold mb-6">{task ? 'Aufgabe bearbeiten' : 'Neue Aufgabe erstellen'}</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <input type="text" placeholder="Aufgabentitel" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-4 py-2 border rounded-lg" required />
                        <textarea placeholder="Beschreibung (optional)" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-4 py-2 border rounded-lg" rows="3"></textarea>
                        
                        {/* Projekt Auswahl statt Textfeld */}
                        <div className="w-full">
                            <ProjectAutocomplete 
                                companyId={companyId} 
                                userUid={userId} 
                                initialValue={projectId || ''} // Nutze ID für Initialisierung, falls vorhanden
                                onSelect={(p) => {
                                    setProjectId(p.id);
                                    setProjectName(p.name);
                                }}
                                onNameResolved={(name) => setProjectName(name)} // Update Name wenn ID aufgelöst wird
                                label="Projekt (optional)"
                            />
                        </div>

                        <AssigneeAutocomplete users={companyUsers} teams={teams} initialValue={assignee?.name} onSelect={setAssignee} />
                        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-4 py-2 border rounded-lg" />
                        <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-4 py-2 border rounded-lg">
                            <option value="Offen">Offen</option>
                            <option value="In Bearbeitung">In Bearbeitung</option>
                            <option value="Erledigt">Erledigt</option>
                        </select>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-300 rounded-lg">Abbrechen</button>
                            <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg">Speichern</button>
                        </div>
                    </form>
                </div>
                {task && (
                    <div className="w-full md:w-64 flex-shrink-0 mt-8 md:mt-0">
                        <h3 className="text-lg font-bold mb-3 border-b pb-2">Verlauf</h3>
                        <div className="space-y-3 text-sm max-h-96 overflow-y-auto pr-2">
                            {task.createdAt && (
                                <div className="text-gray-600 dark:text-gray-400">
                                    <p>Erstellt von <strong>{creatorName}</strong></p>
                                    <p>{new Date(task.createdAt).toLocaleString('de-DE')}</p>
                                </div>
                            )}
                            {task.history?.slice().reverse().map((entry, index) => (
                                <div key={index} className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                    <p className="font-semibold">{entry.user}</p>
                                    <p className="text-gray-600 dark:text-gray-400">{entry.action}</p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500">{new Date(entry.timestamp).toLocaleString('de-DE')}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </CustomModal>
    );
};


const TaskDashboard = ({ userId, companyId, setNotification, companyUsers, teams, userRole, userProfile, tasks }) => {
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    // *** ÄNDERUNG: Standardfilter auf 'Offen' gesetzt ***
    const [statusFilter, setStatusFilter] = useState('Offen');
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [currentTask, setCurrentTask] = useState(null);
    const [activeTab, setActiveTab] = useState('assignedToMe');
    // *** ENTFERNT: State für Lösch-Modal ***
    // const [taskToDelete, setTaskToDelete] = useState(null);

    useEffect(() => {
        let currentTasks = tasks || [];
        if (searchTerm) {
            currentTasks = currentTasks.filter(task =>
                task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        if (statusFilter !== 'all') {
            currentTasks = currentTasks.filter(task => task.status === statusFilter);
        }
        setFilteredTasks(currentTasks);
    }, [tasks, searchTerm, statusFilter]);

    const handleSaveTask = async (taskFromModal) => {
        const timestamp = new Date().toISOString();
        const userName = userProfile.firstName ? `${userProfile.firstName} ${userProfile.lastName}`.trim() : 'Unbekannter Benutzer';
        
        let dataToSave;
        const newHistory = [];

        if (!currentTask) { // Neue Aufgabe
            dataToSave = { 
                ...taskFromModal,
                createdAt: timestamp,
                creatorUserId: userId,
                history: [{ timestamp, user: userName, action: 'Aufgabe erstellt.' }]
            };
        } else { // Bestehende Aufgabe
            const oldTask = currentTask;
            if (oldTask.title !== taskFromModal.title) newHistory.push({ timestamp, user: userName, action: `Titel von "${oldTask.title}" zu "${taskFromModal.title}" geändert.` });
            if (oldTask.description !== taskFromModal.description) newHistory.push({ timestamp, user: userName, action: 'Beschreibung geändert.' });
            if (oldTask.dueDate !== taskFromModal.dueDate) newHistory.push({ timestamp, user: userName, action: `Fälligkeitsdatum von "${oldTask.dueDate || 'keins'}" zu "${taskFromModal.dueDate || 'keins'}" geändert.` });
            if (oldTask.status !== taskFromModal.status) newHistory.push({ timestamp, user: userName, action: `Status von "${oldTask.status}" zu "${taskFromModal.status}" geändert.` });
            // *** KORREKTUR: 'oldOldTask' zu 'oldTask' geändert ***
            if (oldTask.project !== taskFromModal.project) newHistory.push({ timestamp, user: userName, action: `Projekt von "${oldTask.project || 'keins'}" zu "${taskFromModal.project || 'keins'}" geändert.` });
            if (oldTask.assignees?.[0]?.id !== taskFromModal.assignees?.[0]?.id) newHistory.push({ timestamp, user: userName, action: `Zuständigkeit von "${oldTask.assignees?.[0]?.name || 'niemand'}" zu "${taskFromModal.assignees?.[0]?.name || 'niemand'}" geändert.` });

            dataToSave = { ...oldTask, ...taskFromModal };
            if (newHistory.length > 0) dataToSave.history = [...(oldTask.history || []), ...newHistory];
        }

        const finalAssignee = dataToSave.assignees?.[0];
        if (finalAssignee) {
            if (finalAssignee.type === 'user') dataToSave.assigneeUids = [finalAssignee.id];
            else if (finalAssignee.type === 'team') {
                const team = teams.find(t => t.id === finalAssignee.id);
                dataToSave.assigneeUids = team ? team.members : [];
            }
        } else {
            dataToSave.assigneeUids = [];
        }
        
        // *** ÄNDERUNG: isCompleted-Flag synchronisieren ***
        if (dataToSave.status === 'Erledigt') {
            dataToSave.isCompleted = true;
        } else {
            dataToSave.isCompleted = false;
        }


        try {
            await saveTask(dataToSave, companyId, userId);
            setNotification({ message: 'Aufgabe erfolgreich gespeichert!', type: 'success' });
            setShowTaskModal(false);
            setCurrentTask(null);
        } catch (error) {
            setNotification({ message: 'Fehler beim Speichern der Aufgabe.', type: 'error' });
            console.error("Error saving task: ", error);
        }
    };
    
    // *** ENTFERNT: Komplette Lösch-Handler (handleDeleteTask, confirmDeleteTask) ***

    const tasksToDisplay = filteredTasks.filter(task => {
        const isAssignedToMe = task.assigneeUids?.includes(userId);
        const isCreatedByMe = task.creatorUserId === userId;
        if (activeTab === 'assignedToMe') return isAssignedToMe;
        if (activeTab === 'createdByMe') return isCreatedByMe && !isAssignedToMe;
        return false;
    });

    return (
        <div className="p-4 sm:p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-3xl font-bold">Aufgabenübersicht</h2>
                <button onClick={() => { setCurrentTask(null); setShowTaskModal(true); }} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-md transition-transform transform hover:scale-105">
                    + Neue Aufgabe
                </button>
            </div>
            
            <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                    <button onClick={() => setActiveTab('assignedToMe')} className={`${activeTab === 'assignedToMe' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}>
                        Mir zugewiesen
                    </button>
                    <button onClick={() => setActiveTab('createdByMe')} className={`${activeTab === 'createdByMe' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}>
                        Von mir erstellt
                    </button>
                </nav>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <input type="text" placeholder="Aufgaben durchsuchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-2 border rounded-lg" />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-4 py-2 border rounded-lg">
                    <option value="all">Alle Status</option>
                    <option value="Offen">Offen</option>
                    <option value="In Bearbeitung">In Bearbeitung</option>
                    <option value="Erledigt">Erledigt</option>
                </select>
            </div>

            <div className="space-y-4">
                {tasksToDisplay.length > 0 ? tasksToDisplay.map(task => {
                    const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'Erledigt';
                    return (
                        <div key={task.id} className="p-4 bg-gray-100 dark:bg-slate-800 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                            <div className="flex-grow">
                                <div className="flex justify-between items-baseline">
                                    <button onClick={() => { setCurrentTask(task); setShowTaskModal(true); }} className="font-bold text-lg text-blue-600 hover:underline text-left">
                                        {task.title}
                                    </button>
                                    {task.dueDate && <span className={`text-sm flex-shrink-0 ml-4 ${isOverdue ? 'text-red-500 font-bold' : 'text-gray-500 dark:text-gray-400'}`}>Fällig: {new Date(task.dueDate).toLocaleDateString('de-DE')}</span>}
                                </div>
                                {task.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">{task.description}</p>}
                                {task.meetingTitle && (
                                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 3h3m-6-3h.01M9 16h.01" /></svg>
                                        <span>Aus Protokoll: <strong>{task.meetingTitle}</strong></span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                                 <span className={`text-xs font-semibold px-2 py-1 rounded-full inline-block ${ task.status === 'Erledigt' ? 'bg-green-200 text-green-800' : task.status === 'In Bearbeitung' ? 'bg-yellow-200 text-yellow-800' : 'bg-red-200 text-red-800' }`}>{task.status}</span>
                                 {/* *** ENTFERNT: Lösch-Button *** */}
                                 {/* <button onClick={() => handleDeleteTask(task)} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full text-lg">🗑️</button> */}
                            </div>
                        </div>
                    );
                }) : (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-8">Keine Aufgaben für diese Ansicht gefunden.</p>
                )}
            </div>
            {showTaskModal && <TaskModal task={currentTask} onSave={handleSaveTask} onClose={() => setShowTaskModal(false)} companyUsers={companyUsers} teams={teams} userId={userId} companyId={companyId} setNotification={setNotification} />}
            
            {/* *** ENTFERNT: Lösch-Bestätigungsmodal *** */}
        </div>
    );
};

export default TaskDashboard;